import { useCallback, useEffect, useRef, useState } from 'react';
import { CloudUpload } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  LinearProgress,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { beginMigrationUpload } from '../hooks/use-migration-upload-activity';
import type { PresignedRawUploadFile } from '../store/hooks';
import { usePresignRawUploadsMutation } from '../store/hooks';

interface UploadFile {
  file: File;
  path: string;
}

interface OrganizationDataImportV2SectionProps {
  organizationId: string;
  /** Called after a successful upload so the imports list can refresh. */
  onUploaded?: () => void;
  /**
   * When set, append the files to this existing batch (raw upload id) instead
   * of creating a new one. Allowed only while the batch is still pre-conversion.
   */
  uploadId?: string;
  /** Render just the drop zone, without the surrounding Card chrome (e.g. in a dialog). */
  embedded?: boolean;
}

/** How many direct-to-S3 PUTs to run at once (folder drops can be hundreds of files). */
const MAX_CONCURRENT_UPLOADS = 8;

/**
 * How many filenames to presign per request. Presigned PUT URLs expire 15
 * minutes after minting, so a large drop must be presigned in waves sized to
 * upload well inside that window — presigning everything up front hands most
 * of a big drop dead URLs (GDN-2808). One wave is also small enough that the
 * presign request/response stays far from the gateway limits.
 */
const PRESIGN_WAVE_SIZE = 400;

/**
 * Byte cap per wave, so a wave of large files can't outlive the 15-minute URL
 * TTL just because 400 of them add up to gigabytes. ~256MB finishes inside the
 * window even on modest office uplinks.
 */
const PRESIGN_WAVE_MAX_BYTES = 256 * 1024 * 1024;

/** How many times to retry a single file's PUT before marking it failed. */
const PUT_RETRIES = 1;

/**
 * After the main pass, files that still failed (transient errors, or URLs
 * that aged out because a wave ran long) get re-presigned with FRESH URLs and
 * re-uploaded, up to this many rounds. Bounded so a systemic failure — S3
 * outage, revoked credentials — still terminates and reports honestly.
 */
const RECOVERY_ROUNDS = 2;

interface UploadProgress {
  completed: number;
  total: number;
  percent: number;
}

const isFileEntry = (e: FileSystemEntry): e is FileSystemFileEntry => e.isFile;
const isDirectoryEntry = (e: FileSystemEntry): e is FileSystemDirectoryEntry => e.isDirectory;

/** Read every entry of a directory (readEntries returns at most ~100/call). */
function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const acc: FileSystemEntry[] = [];
    const readBatch = (): void => {
      reader.readEntries(batch => {
        if (batch.length === 0) {
          resolve(acc);
        } else {
          acc.push(...batch);
          readBatch();
        }
      }, reject);
    };
    readBatch();
  });
}

/** Recursively flatten a dropped file/folder entry into files + relative paths. */
async function entryToFiles(entry: FileSystemEntry, prefix: string): Promise<UploadFile[]> {
  if (isFileEntry(entry)) {
    const file = await new Promise<File>((resolve, reject) => entry.file(resolve, reject));
    return [{ file, path: `${prefix}${file.name}` }];
  }
  if (isDirectoryEntry(entry)) {
    const entries = await readAllEntries(entry.createReader());
    const groups = await Promise.all(entries.map(e => entryToFiles(e, `${prefix}${entry.name}/`)));
    return groups.flat();
  }
  return [];
}

/**
 * PUT a single file's bytes directly to its presigned S3 URL. Uses
 * XMLHttpRequest (not fetch) so we can report real upload progress. Sends ONLY
 * the presigned headers + the body — no auth header (the URL carries its auth).
 */
function putFileToS3(
  target: PresignedRawUploadFile,
  file: File,
  onProgress: (loaded: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', target.url, true);
    for (const [name, value] of Object.entries(target.headers)) {
      xhr.setRequestHeader(name, value);
    }
    xhr.upload.onprogress = event => {
      if (event.lengthComputable) onProgress(event.loaded);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onabort = () => reject(new Error('Upload aborted'));
    xhr.send(file);
  });
}

/** Run async tasks with a bounded concurrency pool, preserving call order. */
async function runWithConcurrency(tasks: Array<() => Promise<void>>, limit: number): Promise<void> {
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
    while (next < tasks.length) {
      const index = next;
      next += 1;
      await tasks[index]();
    }
  });
  await Promise.all(workers);
}

/**
 * Raw source-file upload for the migration agent. Drag & drop files or a whole
 * folder (or browse) — each file is uploaded straight to the S3 landing zone
 * (v2 raw prefix) via a presigned PUT, so the bytes never stream through the app
 * server (which used to time out on large files). Folder structure is preserved.
 * What happens to an upload afterward (convert → stage → commit) shows in the
 * Imports list below.
 */
export function OrganizationDataImportV2Section({
  organizationId,
  onUploaded,
  uploadId,
  embedded = false,
}: OrganizationDataImportV2SectionProps) {
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const uploadInFlight = useRef(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [presignRawUploads] = usePresignRawUploadsMutation();

  // `webkitdirectory` isn't a typed React attribute — set it imperatively.
  useEffect(() => {
    if (folderInputRef.current) {
      folderInputRef.current.setAttribute('webkitdirectory', '');
      folderInputRef.current.setAttribute('directory', '');
    }
  }, []);

  const uploadItems = useCallback(
    async (items: UploadFile[]) => {
      if (items.length === 0 || !organizationId || uploadInFlight.current) return;
      uploadInFlight.current = true;
      setUploading(true);
      const finishUpload = beginMigrationUpload(organizationId);
      setProgress({ completed: 0, total: items.length, percent: 0 });
      try {
        // Presign + upload in WAVES: URLs expire 15 minutes after minting, so
        // a large drop is presigned a wave at a time and each wave's bytes go
        // up immediately (GDN-2808). Wave 1 mints the batch (or appends to
        // `uploadId` with server-side duplicate skipping); later waves pass
        // `continuation` so the server skips re-enumerating the growing batch.
        const byPath = new Map(items.map(item => [item.path, item.file]));
        const totalBytes = items.reduce((sum, item) => sum + item.file.size, 0);
        const loadedByKey = new Map<string, number>();
        const failures: string[] = [];
        const skippedNames: string[] = [];
        let batchId = uploadId;
        let completed = 0;
        let targetCount = 0;

        const reportProgress = () => {
          const loaded = [...loadedByKey.values()].reduce((a, b) => a + b, 0);
          const percent = totalBytes > 0 ? Math.round((loaded / totalBytes) * 100) : 100;
          setProgress({ completed, total: items.length, percent });
        };

        // Waves are capped by file count AND bytes: either bound alone lets a
        // pathological wave (many files, or few huge ones) outlive the URL TTL.
        const buildWaves = (source: UploadFile[]): UploadFile[][] => {
          const result: UploadFile[][] = [];
          let wave: UploadFile[] = [];
          let waveBytes = 0;
          for (const item of source) {
            const overCount = wave.length >= PRESIGN_WAVE_SIZE;
            const overBytes =
              wave.length > 0 && waveBytes + item.file.size > PRESIGN_WAVE_MAX_BYTES;
            if (overCount || overBytes) {
              result.push(wave);
              wave = [];
              waveBytes = 0;
            }
            wave.push(item);
            waveBytes += item.file.size;
          }
          if (wave.length > 0) result.push(wave);
          return result;
        };
        const waves = buildWaves(items);

        // 1) Presign one wave. The first wave establishes the batch id; every
        // later wave is a continuation against it.
        const presignWave = async (wave: UploadFile[], isFirst: boolean) => {
          const presign = await presignRawUploads({
            organization_id: organizationId,
            files: wave.map(item => ({ filename: item.path })),
            ...(batchId ? { upload_id: batchId } : {}),
            ...(!isFirst ? { continuation: true } : {}),
          }).unwrap();
          batchId = presign.upload_id;
          for (const name of presign.skipped ?? []) {
            skippedNames.push(name);
            // A skipped file uploads nothing — count its bytes as done so the
            // progress bar still reaches 100%.
            loadedByKey.set(`skipped:${name}`, byPath.get(name)?.size ?? 0);
            completed += 1;
          }
          reportProgress();
          // Pair each returned URL with its File (matched by relative path).
          return (presign.files ?? [])
            .map(meta => ({ meta, file: byPath.get(meta.filename) }))
            .filter((t): t is { meta: PresignedRawUploadFile; file: File } => t.file !== undefined);
        };

        // 2) PUT a wave's bytes directly to S3, tracking real progress.
        const uploadWave = async (
          targets: Array<{ meta: PresignedRawUploadFile; file: File }>,
          recovery = false,
        ) => {
          // Recovery re-attempts files already counted by the main pass, so
          // it must not inflate the totals a second time.
          if (!recovery) targetCount += targets.length;
          await runWithConcurrency(
            targets.map(t => async () => {
              try {
                for (let attempt = 0; ; attempt += 1) {
                  try {
                    await putFileToS3(t.meta, t.file, loaded => {
                      loadedByKey.set(t.meta.key, loaded);
                      reportProgress();
                    });
                    break;
                  } catch (err) {
                    if (attempt >= PUT_RETRIES) throw err;
                  }
                }
                loadedByKey.set(t.meta.key, t.file.size);
              } catch {
                failures.push(t.meta.filename);
              } finally {
                if (!recovery) completed += 1;
                reportProgress();
              }
            }),
            MAX_CONCURRENT_UPLOADS,
          );
        };

        // 3) Pipeline: while wave N uploads, wave N+1's URLs are minted, so
        // every URL is used within minutes of signing.
        let nextPresign = presignWave(waves[0], true);
        for (let i = 0; i < waves.length; i += 1) {
          let targets: Array<{ meta: PresignedRawUploadFile; file: File }> = [];
          try {
            targets = await nextPresign;
          } catch (err) {
            // Before the batch exists nothing has started, so the generic
            // failure toast is accurate — let the outer catch have it. After
            // that, a failed presign RPC is just this wave failing: its files
            // join `failures` (counted like PUT failures, cured by recovery)
            // instead of one bad RPC discarding hours of partial progress.
            if (batchId === undefined) throw err;
            targetCount += waves[i].length;
            completed += waves[i].length;
            failures.push(...waves[i].map(item => item.path));
            reportProgress();
          }
          if (i + 1 < waves.length) {
            nextPresign = presignWave(waves[i + 1], false);
            // The real handler is the `await` on the next iteration; this
            // no-op keeps a rejection during the current wave's upload from
            // surfacing as an unhandled-rejection in the meantime.
            nextPresign.catch(() => undefined);
          }
          await uploadWave(targets);
        }

        // 4) Recovery: anything that failed — a transient error, or a URL
        // that aged out because its wave ran long — gets a FRESH presigned
        // URL and another pass. This, not the presign pipelining, is what
        // guarantees no file is ever abandoned to an expired URL.
        for (let round = 0; round < RECOVERY_ROUNDS && failures.length > 0; round += 1) {
          const retryItems = failures.flatMap(path => {
            const file = byPath.get(path);
            return file ? [{ path, file }] : [];
          });
          failures.length = 0;
          for (const wave of buildWaves(retryItems)) {
            try {
              const targets = await presignWave(wave, false);
              await uploadWave(targets, true);
            } catch {
              // Presign RPC failed for this recovery wave — put its files
              // back so the next round (or the final toast) accounts for
              // them instead of unwinding the whole run.
              failures.push(...wave.map(item => item.path));
            }
          }
        }

        // 5) Surface the outcome and refresh the uploads list.
        const succeeded = targetCount - failures.length;
        const skipped = skippedNames.length;
        const verb = uploadId ? 'Added' : 'Uploaded';

        if (failures.length > 0) {
          enqueueSnackbar(
            `${verb} ${succeeded} of ${targetCount} file(s); ${failures.length} failed`,
            { variant: 'error' },
          );
        } else {
          enqueueSnackbar(
            skipped
              ? `${verb} ${succeeded} file(s), skipped ${skipped} duplicate(s)`
              : `${verb} ${succeeded} file(s)`,
            { variant: skipped ? 'warning' : 'success' },
          );
        }
        onUploaded?.();
      } catch (err) {
        enqueueSnackbar(err instanceof Error ? err.message : 'Upload failed', {
          variant: 'error',
        });
      } finally {
        uploadInFlight.current = false;
        setUploading(false);
        finishUpload();
        setProgress(null);
      }
    },
    [organizationId, uploadId, enqueueSnackbar, onUploaded, presignRawUploads],
  );

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    const dt = e.dataTransfer;
    // Grab entries synchronously — they become invalid after the handler ends.
    const entries: FileSystemEntry[] = [];
    for (const item of Array.from(dt.items)) {
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry();
        if (entry) entries.push(entry);
      }
    }
    const plainFiles = Array.from(dt.files);
    void (async () => {
      let collected: UploadFile[];
      if (entries.length > 0) {
        const groups = await Promise.all(entries.map(en => entryToFiles(en, '')));
        collected = groups.flat();
      } else {
        collected = plainFiles.map(file => ({ file, path: file.name }));
      }
      await uploadItems(collected);
    })();
  };

  const onPicked = (list: FileList | null) => {
    if (!list) return;
    const collected = Array.from(list).map(file => ({
      file,
      path: file.webkitRelativePath || file.name,
    }));
    void uploadItems(collected);
  };

  const dropZone = (
    <>
      <input
        ref={folderInputRef}
        type="file"
        hidden
        onChange={e => {
          onPicked(e.target.files);
          e.target.value = '';
        }}
      />

      <Box
        onDragOver={e => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={e => {
          e.preventDefault();
          setDragActive(false);
        }}
        onDrop={onDrop}
        sx={{
          border: '2px dashed',
          borderColor: dragActive ? 'primary.main' : 'divider',
          borderRadius: 1,
          p: 3,
          textAlign: 'center',
          bgcolor: dragActive ? 'action.hover' : 'transparent',
        }}
      >
        <CloudUpload color="action" />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Drag &amp; drop files or a folder here
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
            disabled={uploading || !organizationId}
            onClick={() => folderInputRef.current?.click()}
          >
            {uploading ? 'Uploading…' : 'Choose files'}
          </Button>
        </Box>

        {uploading && progress && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress.percent} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Uploading {progress.completed}/{progress.total} files… {progress.percent}%
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );

  if (embedded) {
    return dropZone;
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Upload source files"
        subheader="Drop source files or a folder here. When the upload finishes, press Go in Imports to start conversion."
      />
      <CardContent>{dropZone}</CardContent>
    </Card>
  );
}
